import React, { useContext, useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import { useLocation } from "react-router-dom";
import launc from "../../../images/Campaign-01 1.png";
import useCampaigns from "../../../services/Whatsapp/useCampaigns";
import useContacts from "../../../services/chat/useContacts";
import useAudience from "../../../services/Whatsapp/useAudience";
import useTemplates from "../../../services/Whatsapp/useTemplates";

import CampaignSchedule from "../Create Campaign/CampaignSchedule";
import CampaignAudience from "../Create Campaign/CampaignAudience";
import CampaignTemplate from "../Create Campaign/CampaignTemplate";
import CampaignTesting from "../Create Campaign/CampaignTesting";
import { GlobalContext } from "../../../Context/GlobalContext";

const steps = [
  { id: 1, title: "Schedule" },
  { id: 2, title: "Audience" },
  { id: 3, title: "Template" },
  { id: 4, title: "Testing" },
  { id: 5, title: "Save" },
];

const CreateCampaign = () => {
  const { userID } = useContext(GlobalContext);
  const { createCampaign, loading } = useCampaigns();
  const { chatContacts: contacts } = useContacts();
  const { audienceGroups, createAudienceGroup } = useAudience();
  const { templates } = useTemplates();
  const location = useLocation();

  const getInitialFormData = () => ({
    campaignName: "",
    campaignType: "schedule",
    campPurpose: "",

    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    interval: "",
    intervalUnit: "minutes",

    phoneNumberId: "",
    audience: [],
    template: {},
    fileUrl: "",

    templateName: "",
    templateID: "",
    templateLanguage: "",
    templateStatus: "",
    templateCategory: "",
    headerParams: [],
    bodyParams: [],
  });

  const [step, setStep] = useState(1);
  const [selectedAudienceGroup, setSelectedAudienceGroup] = useState(null);
  const [formData, setFormData] = useState(getInitialFormData());

  useEffect(() => {
    if (location.state?.startStep) {
      setStep(location.state.startStep);
    }
  }, [location.state]);

  const handleSelectContact = (contact) => {
    setFormData((prev) => {
      const alreadySelected = prev.audience.some(
        (item) => String(item.id) === String(contact.id),
      );

      if (alreadySelected) return prev;

      return {
        ...prev,
        audience: [...prev.audience, contact],
      };
    });
  };

  const handleDeselectContact = (contact) => {
    setFormData((prev) => ({
      ...prev,
      audience: prev.audience.filter(
        (item) => String(item.id) !== String(contact.id),
      ),
    }));
  };

  const formatDateTime = (date, time) => {
    if (!date || !time) return " :00";
    return `${date} ${time}:00`;
  };

  const convertToMinutes = (value, unit) => {
    const numericValue = Number(value || 0);

    switch (unit) {
      case "minutes":
        return numericValue;
      case "hours":
        return numericValue * 60;
      case "days":
        return numericValue * 60 * 24;
      default:
        return numericValue;
    }
  };

  const handleFinalSave = async () => {
    if (!formData.campaignName) {
      toast.error("Campaign name required");
      return;
    }

    if (!formData.audience?.length) {
      toast.error("Please select audience");
      return;
    }

    if (!formData.template?.id) {
      toast.error("Please select template");
      return;
    }

    const payload = {
      userid: userID,
      campaignName: formData.campaignName,
      purpose: formData.campPurpose || formData.purpose || "",

      startTime: formatDateTime(formData.startDate, formData.startTime),
      endTime:
        formData.campaignType === "instant"
          ? " :00"
          : formatDateTime(formData.endDate, formData.endTime),

      interval:
        formData.campaignType === "instant"
          ? ""
          : convertToMinutes(formData.interval, formData.intervalUnit),

      channel: "Whatsapp",

      language_code:
        formData.templateLanguage || formData.template?.language || "en",

      members: formData.audience.map((audienceItem) => ({
        member_id: audienceItem.id,
        name: audienceItem.contact_name || audienceItem.name,
        mobile: audienceItem.contact_number || audienceItem.mobile,
      })),

      template: formData.templateName || formData.template?.name,
      template_category:
        formData.templateCategory || formData.template?.category,
      templateMetaId: formData.templateID || formData.template?.id,

      headerParams: formData.headerParams || [],
      bodyParams: formData.bodyParams || [],
      instant: formData.campaignType === "instant" ? "Y" : "N",
    };

    console.log("Final Campaign Payload:", payload);

    const res = await createCampaign(payload);

    if (res?.responseCode === "00") {
      toast.success("🎉 Campaign Created Successfully!");

      setTimeout(() => {
        setFormData(getInitialFormData());
        setSelectedAudienceGroup(null);
        setStep(1);
      }, 1500);
    } else {
      toast.error(res?.responseMessage || "Failed");
    }
  };

  return (
    <>
      <ToastContainer />

      {/* 
        IMPORTANT:
        64px ko apne navbar/header ki actual height ke according change karein.
        Navbar 82px ka hai to calc(100dvh - 82px) use karein.
      */}
      <div
        className="w-full min-h-0 overflow-x-hidden overflow-y-auto overscroll-contain p-2 sm:p-4 lg:p-6 montserrat-font-1"
        style={{
          height: "calc(100dvh - 64px)",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div className="mx-auto flex w-full min-w-0 max-w-[1600px] flex-col gap-5 rounded-xl bg-white p-3 shadow sm:p-4 lg:flex-row lg:gap-10 lg:p-6">
          {/* Stepper */}
          <aside className="w-full min-w-0 border-b pb-4 lg:w-64 lg:flex-shrink-0 lg:border-b-0 lg:border-r lg:pb-0 lg:pr-6">
            <h2 className="mb-4 text-lg font-semibold lg:mb-6">
              Create Campaign
            </h2>

            <div className="w-full overflow-x-auto pb-2 lg:overflow-visible lg:pb-0">
              <div className="flex min-w-max gap-5 lg:block lg:min-w-0 lg:space-y-10">
                {steps.map((currentStep, index) => (
                  <div
                    key={currentStep.id}
                    className="relative flex min-w-[112px] items-start gap-3 lg:min-w-0 lg:gap-6"
                  >
                    {index !== steps.length - 1 && (
                      <div className="absolute left-7 top-14 hidden h-16 w-[2px] bg-gray-200 lg:block" />
                    )}

                    <div
                      className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold sm:h-12 sm:w-12 lg:h-14 lg:w-14 ${
                        step === currentStep.id
                          ? "bg-blue-600 text-white"
                          : step > currentStep.id
                            ? "bg-green-500 text-white"
                            : "bg-gray-200 text-gray-500"
                      }`}
                    >
                      {currentStep.id}
                    </div>

                    <div className="min-w-0 pt-1">
                      <p className="whitespace-nowrap font-medium">
                        {currentStep.title}
                      </p>
                      <p className="whitespace-nowrap text-xs text-gray-500">
                        {step === currentStep.id
                          ? "In Progress"
                          : step > currentStep.id
                            ? "Complete"
                            : "Pending"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </aside>

          {/* Content */}
          <main className="min-w-0 flex-1">
            {step === 1 && (
              <CampaignSchedule
                formData={formData}
                setFormData={setFormData}
                next={() => setStep(2)}
              />
            )}

            {step === 2 && (
              <CampaignAudience
                contacts={contacts}
                audience={audienceGroups}
                createAudienceGroup={createAudienceGroup}
                selectedAudienceGroup={selectedAudienceGroup}
                handleAudienceGroupChange={(e) => {
                  const selectedId = e.target.value;

                  if (!selectedId) {
                    setSelectedAudienceGroup(null);
                    return;
                  }

                  const group = audienceGroups.find(
                    (item) => String(item.id) === String(selectedId),
                  );

                  setSelectedAudienceGroup(group || null);
                }}
                selectedItems={formData.audience}
                handleSelect={handleSelectContact}
                handleDeselect={handleDeselectContact}
                next={() => setStep(3)}
                back={() => setStep(1)}
              />
            )}

            {step === 3 && (
              <CampaignTemplate
                formData={formData}
                setFormData={setFormData}
                templates={templates}
                next={() => setStep(4)}
                back={() => setStep(2)}
              />
            )}

            {step === 4 && (
              <CampaignTesting
                formData={formData}
                back={() => setStep(3)}
                next={() => setStep(5)}
              />
            )}

            {step === 5 && (
              <div className="grid min-w-0 grid-cols-1 items-center gap-8 py-6 lg:grid-cols-12 lg:gap-12 lg:py-12 xl:gap-16">
                <div className="space-y-5 text-center lg:col-span-7">
                  <div className="flex justify-center">
                    <img
                      src={launc}
                      alt="Launch"
                      className="h-auto w-full max-w-96"
                    />
                  </div>

                  <h2 className="text-2xl font-semibold tracking-wide sm:text-3xl lg:text-4xl">
                    Congratulations!
                  </h2>

                  <p className="mx-auto max-w-lg text-sm leading-relaxed text-gray-600 sm:text-base montserrat-font-2">
                    You have successfully created your campaign. What's next?
                    Are you excited to launch it?
                  </p>

                  <div className="flex flex-col-reverse justify-center gap-3 pt-4 sm:flex-row sm:gap-6 lg:pt-8">
                    <button
                      type="button"
                      onClick={() => setStep(4)}
                      className="w-full rounded-md bg-gray-300 px-10 py-2 transition hover:bg-gray-400 sm:w-auto"
                    >
                      Previous
                    </button>

                    <button
                      type="button"
                      onClick={handleFinalSave}
                      disabled={loading}
                      className="w-full rounded-md bg-blue-600 px-12 py-2 text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                    >
                      {loading ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>

                <div className="min-w-0 lg:col-span-5">
                  <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50 shadow-sm">
                    <div className="border-b bg-gray-100 px-4 py-4 sm:px-6">
                      <h3 className="text-lg font-semibold">
                        Campaign Details
                      </h3>
                    </div>

                    <div className="divide-y text-sm">
                      <div className="flex flex-col gap-1 px-4 py-4 sm:flex-row sm:justify-between sm:gap-4 sm:px-6">
                        <span className="font-medium text-gray-700">
                          Campaign Name
                        </span>
                        <span className="break-words text-gray-800">
                          {formData.campaignName}
                        </span>
                      </div>

                      <div className="flex flex-col gap-1 px-4 py-4 sm:flex-row sm:justify-between sm:gap-4 sm:px-6">
                        <span className="font-medium text-gray-700">
                          Audience Size
                        </span>
                        <span className="text-gray-800">
                          {formData.audience.length}
                        </span>
                      </div>

                      <div className="flex flex-col gap-1 px-4 py-4 sm:flex-row sm:justify-between sm:gap-4 sm:px-6">
                        <span className="font-medium text-gray-700">
                          Schedule Date &amp; Time
                        </span>
                        <span className="break-words text-gray-800">
                          {formData.startDate} {formData.startTime}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </>
  );
};

export default CreateCampaign;
